import { useEffect, useState } from 'react'
import api from '../services/api'

// Certificados/Registros do funcionário (frota.vctmot) — certificados tipo F do cadastro TVCTCAD

interface Linha { vct: number; des?: string | null; val: string; num: string; nova?: boolean }
interface Opcao { vct: number; des: string; ati: string }

const th: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.6rem', fontSize: '0.72rem' }
const td: React.CSSProperties = { padding: '0.3rem 0.5rem', fontSize: '0.82rem' }
const inp: React.CSSProperties = { boxSizing: 'border-box', padding: '0.35rem 0.5rem', fontSize: '0.82rem' }

const hojeIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' })
const em30 = () => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }) }

function situacao(val: string): { texto: string; cor: string; bg: string } | null {
  if (!val) return null
  if (val < hojeIso()) return { texto: 'Vencido', cor: '#b91c1c', bg: '#fee2e2' }
  if (val <= em30()) return { texto: 'Vence em 30 dias', cor: '#b45309', bg: '#fef3c7' }
  return null
}

export default function FuncionarioCertificadosModal({ motcod, motnome, onClose }: { motcod: string; motnome: string; onClose: () => void }) {
  const [lista, setLista] = useState<Linha[] | null>(null)
  const [opcoes, setOpcoes] = useState<Opcao[]>([])
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function carregar() {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/certificados`)
    setLista(r.data.lista)
    setOpcoes(r.data.opcoes)
  }

  useEffect(() => {
    carregar().catch(() => setErro('Não foi possível carregar os certificados.'))
  }, [motcod])

  async function salvar() {
    setErro(''); setAviso('')
    if (lista!.some(l => l.nova && !l.vct)) return setErro('Escolha o certificado da linha nova (ou exclua a linha).')
    setSalvando(true)
    try {
      const r = await api.put(`/funcionarios/${encodeURIComponent(motcod)}/certificados`, { lista })
      await carregar()
      setAviso(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Certificados salvos.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar os certificados.')
    } finally {
      setSalvando(false)
    }
  }

  const setLinha = (i: number, campo: keyof Linha, v: string | number) =>
    setLista(ls => ls!.map((l, j) => (j === i ? { ...l, [campo]: v } : l)))
  const usados = new Set((lista ?? []).map(l => l.vct))

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 820, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}><h2>{`Certificados/Registros — ${motcod} · ${motnome}`}</h2></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>VCTMOT</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo" style={{ overflowY: 'auto' }}>
          {!lista ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : (
            <>
              <table className="table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={th}>Certificado / Registro</th>
                    <th style={{ ...th, width: 150 }}>Validade</th>
                    <th style={th}>Número</th>
                    <th style={{ ...th, width: 70 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lista.length === 0 && (
                    <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#6b7280', padding: '1rem' }}>Nenhum certificado lançado.</td></tr>
                  )}
                  {lista.map((l, i) => {
                    const s = situacao(l.val)
                    return (
                      <tr key={l.nova ? `nova-${i}` : l.vct}>
                        <td style={td}>
                          {l.nova ? (
                            <select value={l.vct || ''} onChange={e => setLinha(i, 'vct', Number(e.target.value))}
                              style={{ ...inp, width: '100%', height: 34, border: '1px solid #e5e7eb', borderRadius: '0.35rem', background: '#f9fafb' }}>
                              <option value="">Escolha...</option>
                              {opcoes.filter(o => o.vct === l.vct || (!usados.has(o.vct) && o.ati !== 'I')).map(o => (
                                <option key={o.vct} value={o.vct}>{o.vct} — {o.des}</option>
                              ))}
                            </select>
                          ) : (
                            <span><b>{l.vct}</b> — {l.des ?? '(não cadastrado)'}</span>
                          )}
                        </td>
                        <td style={td}>
                          <input type="date" value={l.val} style={{ ...inp, width: '100%' }} onChange={e => setLinha(i, 'val', e.target.value)} />
                          {s && <div style={{ marginTop: 3, display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, color: s.cor, background: s.bg, padding: '1px 6px', borderRadius: 4 }}>{s.texto}</div>}
                        </td>
                        <td style={td}>
                          <input value={l.num} maxLength={30} style={{ ...inp, width: '100%' }} onChange={e => setLinha(i, 'num', e.target.value.toUpperCase())} />
                        </td>
                        <td style={td}>
                          <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }} onClick={() => setLista(ls => ls!.filter((_, j) => j !== i))}>Excluir</button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }}
                onClick={() => setLista(ls => [...ls!, { vct: 0, val: '', num: '', nova: true }])}>
                + Novo certificado
              </button>
            </>
          )}

          {lista && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando || !lista}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
